import { useMemo, useState } from 'react';
import {
  Typography,
  Table,
  Space,
  Tag,
  Select,
  DatePicker,
  Button,
  Drawer,
  Descriptions,
  Statistic,
  Divider,
  Empty,
} from 'antd';
import { DollarCircleOutlined, FileSearchOutlined, LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAdminPayments } from '../hooks/usePayments';
import type { PaymentRecord } from '@/shared/types';

const { Title, Text } = Typography;

const statusColors: Record<PaymentRecord['status'], string> = {
  pending: 'gold',
  paid: 'green',
  failed: 'red',
  refunded: 'purple',
};

export const AdminPaymentsPage: React.FC = () => {
  const [filters, setFilters] = useState<{ status: PaymentRecord['status'][]; month: string | null }>({ status: [], month: null });
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);

  const { data: payments = [], isLoading } = useAdminPayments({
    status: filters.status,
    month: filters.month,
    clientId: null,
  });

  const aggregated = useMemo(() => {
    const totalAmount = payments.reduce((sum, payment) => sum + (payment.status === 'paid' ? payment.amount : 0), 0);
    const pendingAmount = payments.reduce((sum, payment) => sum + (payment.status === 'pending' ? payment.amount : 0), 0);
    const byStatus = payments.reduce<Record<string, number>>((acc, payment) => {
      acc[payment.status] = (acc[payment.status] ?? 0) + 1;
      return acc;
    }, {});
    return { totalAmount, pendingAmount, byStatus };
  }, [payments]);

  const columns: ColumnsType<PaymentRecord> = [
    {
      title: 'Cliente',
      dataIndex: 'user',
      key: 'user',
      render: (value) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value?.full_name ?? 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {value?.company_name ?? 'Sem empresa'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: PaymentRecord['status']) => (
        <Tag color={statusColors[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Vencimento',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date: string | null | undefined) => (date ? dayjs(date).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Produto',
      dataIndex: 'product',
      key: 'product',
      render: (value) => value?.name ?? '—',
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<FileSearchOutlined />} onClick={() => setSelectedPayment(record)}>
            Detalhes
          </Button>
          {record.invoice_url && (
            <Button type="link" icon={<LinkOutlined />} href={record.invoice_url} target="_blank">
              Fatura
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            Pagamentos
          </Title>
          <Space>
            <Select
              mode="multiple"
              placeholder="Status"
              value={filters.status}
              onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
              style={{ minWidth: 180 }}
              options={[
                { value: 'pending', label: 'Pendente' },
                { value: 'paid', label: 'Pago' },
                { value: 'failed', label: 'Falha' },
                { value: 'refunded', label: 'Reembolsado' },
              ]}
            />
            <DatePicker
              picker="month"
              allowClear
              placeholder="Competência"
              value={filters.month ? dayjs(filters.month, 'YYYY-MM') : null}
              onChange={(date) => setFilters((prev) => ({ ...prev, month: date ? date.format('YYYY-MM') : null }))}
            />
          </Space>
        </div>

        <Space size="large">
          <Statistic
            title="Receita confirmada"
            value={aggregated.totalAmount}
            prefix={<DollarCircleOutlined />}
            formatter={(value) =>
              Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            }
          />
          <Statistic
            title="Em aberto"
            value={aggregated.pendingAmount}
            formatter={(value) =>
              Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            }
          />
          <Statistic
            title="Pagos"
            value={aggregated.byStatus.paid ?? 0}
          />
          <Statistic
            title="Pendentes"
            value={aggregated.byStatus.pending ?? 0}
          />
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={payments}
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Nenhum pagamento encontrado' }}
        />
      </Space>

      <Drawer
        width={520}
        open={!!selectedPayment}
        onClose={() => setSelectedPayment(null)}
        title="Detalhes da cobrança"
      >
        {!selectedPayment ? (
          <Empty description="Selecione um pagamento" />
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Cliente">
                <Space direction="vertical" size={0}>
                  <Text strong>{selectedPayment.user?.full_name ?? 'N/A'}</Text>
                  <Text type="secondary">{selectedPayment.user?.company_name ?? '—'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Valor">
                {selectedPayment.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[selectedPayment.status]}>{selectedPayment.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Vencimento">
                {selectedPayment.due_date ? dayjs(selectedPayment.due_date).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Criado em">
                {dayjs(selectedPayment.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Workflow">
                {selectedPayment.product?.name ?? '—'}
              </Descriptions.Item>
            </Descriptions>

            {selectedPayment.description && (
              <div>
                <Divider orientation="left">Descrição</Divider>
                <Text>{selectedPayment.description}</Text>
              </div>
            )}

            {selectedPayment.invoice_url && (
              <Button type="primary" icon={<LinkOutlined />} href={selectedPayment.invoice_url} target="_blank">
                Abrir fatura
              </Button>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};
